import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { TextStyle, Color, FontFamily } from "@tiptap/extension-text-style";

import {
  PeopleAlt as PeopleAltIcon,
  Visibility as VisibilityIcon,
  School as SchoolIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Email as EmailIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import StudentBookingHistory from "./StudentBookingHistory";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Alert,
  Box,
  Tabs,
  Tab,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Chip,
} from "@mui/material";

const API = process.env.REACT_APP_API_URL;

const AdminStudentList = () => {
  const [students, setStudents] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [invoiceDialog, setInvoiceDialog] = useState({
    open: false,
    loading: false,
    hostel: [],
    library: [],
    error: "",
    student: null,
    markingPaid: {},
    tab: 0,
  });
  const [generatingInvoice, setGeneratingInvoice] = useState(false);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isSearching, setIsSearching] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchInputRef = useRef(null);

  // Email functionality state
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [emailDialog, setEmailDialog] = useState({
    open: false,
    loading: false,
    subject: "",
    message: "",
    targetGroup: "both",
    sendToAll: false,
    error: "",
  });

  // TipTap editor configuration
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-blue-600 underline",
        },
      }),
      Image.configure({
        HTMLAttributes: {
          class: "max-w-full h-auto",
        },
      }),
      TextStyle,
      Color,
      FontFamily,
    ],
    content: emailDialog.message || "",
    editorProps: {
      attributes: {
        class:
          "prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none",
      },
    },
    onUpdate: ({ editor }) => {
      setEmailDialog((prev) => ({
        ...prev,
        message: editor.getHTML(),
      }));
    },
  });

  // Update editor content when emailDialog.message changes
  React.useEffect(() => {
    if (editor && emailDialog.message !== editor.getHTML()) {
      editor.commands.setContent(emailDialog.message || "");
    }
  }, [editor, emailDialog.message]);

  const token = localStorage.getItem("token");
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const fetchStudents = async (search = "", status = "all") => {
    const loadingToast = toast.loading("Loading students...");
    setIsSearching(true);
    try {
      let url = `${API}users/students/`;
      const params = new URLSearchParams();

      if (search.trim()) {
        params.append("search", search.trim());
      }

      if (status !== "all") {
        params.append("status", status);
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const res = await axios.get(url, config);
      toast.dismiss(loadingToast);
      setStudents(res.data);
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("❌ Failed to fetch students.");
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Fetch students when debounced search or status changes
  useEffect(() => {
    fetchStudents(debouncedSearch, statusFilter);
    // Keep focus in search field after search completes
    if (!isSearching && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, statusFilter]);

  // Handle clear search
  const handleClearSearch = () => {
    setSearchQuery("");
    setStatusFilter("all");
    // Focus will be handled by the useEffect above
  };

  const confirmDelete = (id) => {
    setSelectedStudentId(id);
    setShowConfirm(true);
  };

  const handleViewStudent = (student) => {
    setSelectedStudent(student);
    setViewModalOpen(true);
  };

  const handleDeactivate = async () => {
    const loadingToast = toast.loading("Deactivating student...");
    try {
      await axios.delete(
        `${API}users/delete-user/${selectedStudentId}/`,
        config
      );
      toast.dismiss(loadingToast);
      toast.success("✅ Student deactivated successfully");
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("❌ Failed to deactivate student");
    } finally {
      fetchStudents();
      setShowConfirm(false);
      setSelectedStudentId(null);
    }
  };

  const handleActivate = async (userId) => {
    const loadingToast = toast.loading("Activating student...");
    try {
      await axios.post(`${API}users/activate-user/${userId}/`, {}, config);
      toast.dismiss(loadingToast);
      toast.success("✅ Student activated successfully");
      fetchStudents();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("❌ Failed to activate student");
    }
  };

  // Fetch invoices for a student
  const fetchInvoicesForStudent = async (student) => {
    setInvoiceDialog((d) => ({
      ...d,
      open: true,
      loading: true,
      error: "",
      student,
      markingPaid: {},
      tab: 0,
    }));
    try {
      const hostelRes = await axios.get(
        `${API}hostel/admin/hostel-invoices/?student_id=${student.id}`,
        config
      );
      const libraryRes = await axios.get(
        `${API}library/admin/library-invoices/?student_id=${student.id}`,
        config
      );
      setInvoiceDialog((d) => ({
        ...d,
        hostel: hostelRes.data,
        library: libraryRes.data,
        loading: false,
        error: "",
      }));
    } catch (err) {
      setInvoiceDialog((d) => ({
        ...d,
        loading: false,
        error: "Failed to fetch invoices.",
      }));
    }
  };

  // Mark invoice as paid
  const handleMarkPaid = async (type, invoiceId) => {
    setInvoiceDialog((d) => ({
      ...d,
      markingPaid: { ...d.markingPaid, [invoiceId]: true },
    }));
    try {
      const url =
        type === "hostel"
          ? `${API}hostel/invoices/${invoiceId}/mark-paid/`
          : `${API}library/invoices/${invoiceId}/mark-paid/`;
      await axios.post(url, {}, config);
      toast.success("Invoice marked as paid.");
      // Refresh invoices
      fetchInvoicesForStudent(invoiceDialog.student);
    } catch (err) {
      toast.error("Failed to mark as paid.");
    } finally {
      setInvoiceDialog((d) => ({
        ...d,
        markingPaid: { ...d.markingPaid, [invoiceId]: false },
      }));
    }
  };

  // Generate invoice for the selected student
  const handleGenerateInvoice = async (type) => {
    setGeneratingInvoice(true);
    const loadingToast = toast.loading("Generating invoice...");
    try {
      const url =
        type === "hostel"
          ? `${API}hostel/generate-next-month-invoice/`
          : `${API}library/generate-next-month-invoice/`;
      await axios.post(url, { student_id: invoiceDialog.student.id }, config);
      toast.dismiss(loadingToast);
      toast.success("Invoice generated successfully.");
      fetchInvoicesForStudent(invoiceDialog.student);
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to generate invoice."
      );
    } finally {
      setGeneratingInvoice(false);
    }
  };

  // Email functionality methods
  const handleSelectStudent = (studentId) => {
    setSelectedStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map((student) => student.id));
    }
  };

  const openEmailDialog = () => {
    setEmailDialog({
      open: true,
      loading: false,
      subject: "",
      message: "",
      targetGroup: "both",
      sendToAll:
        selectedStudents.length === students.length && students.length > 0,
      error: "",
    });

    // Update editor content when dialog opens
    if (editor) {
      editor.commands.setContent("");
    }
  };

  const handleSendEmail = async () => {
    // Strip HTML tags for validation
    const plainTextMessage = emailDialog.message.replace(/<[^>]*>/g, "").trim();

    if (!emailDialog.subject.trim() || !plainTextMessage) {
      setEmailDialog((prev) => ({
        ...prev,
        error: "Subject and message are required.",
      }));
      return;
    }

    setEmailDialog((prev) => ({ ...prev, loading: true, error: "" }));
    const loadingToast = toast.loading("Sending email...");

    try {
      // Automatically determine if sending to all based on selection
      const isSendingToAll =
        selectedStudents.length === students.length && students.length > 0;

      const payload = {
        subject: emailDialog.subject,
        message: emailDialog.message,
        target_group: emailDialog.targetGroup,
        send_to_all: isSendingToAll,
      };

      if (!isSendingToAll && selectedStudents.length > 0) {
        payload.recipient_ids = selectedStudents;
      }

      const response = await axios.post(
        `${API}admin/send-email/`,
        payload,
        config
      );

      toast.dismiss(loadingToast);
      toast.success(response.data.message || "Email sent successfully!");

      setEmailDialog((prev) => ({ ...prev, open: false, loading: false }));
      setSelectedStudents([]);
    } catch (err) {
      toast.dismiss(loadingToast);
      const errorMessage =
        err?.response?.data?.error || err?.message || "Failed to send email.";
      toast.error(errorMessage);
      setEmailDialog((prev) => ({
        ...prev,
        loading: false,
        error: errorMessage,
      }));
    }
  };

  // TipTap Toolbar Component
  const TipTapToolbar = () => {
    if (!editor) return null;

    const colors = [
      "#958DF1",
      "#F98181",
      "#FBBC88",
      "#FAF594",
      "#70CFF8",
      "#94FADB",
      "#B9F18D",
    ];

    return (
      <div className="border border-gray-300 rounded-t-lg bg-gray-50 p-2">
        <div className="flex flex-wrap gap-1 items-center">
          {/* Text Formatting */}
          <div className="flex gap-1">
            <button
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                editor.isActive("bold")
                  ? "bg-blue-200 text-blue-800"
                  : "text-gray-700"
              }`}
              title="Bold"
            >
              <strong className="text-sm">B</strong>
            </button>
            <button
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                editor.isActive("italic")
                  ? "bg-blue-200 text-blue-800"
                  : "text-gray-700"
              }`}
              title="Italic"
            >
              <em className="text-sm">I</em>
            </button>
            <button
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                editor.isActive("underline")
                  ? "bg-blue-200 text-blue-800"
                  : "text-gray-700"
              }`}
              title="Underline"
            >
              <u className="text-sm">U</u>
            </button>
            <button
              onClick={() => editor.chain().focus().toggleStrike().run()}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                editor.isActive("strike")
                  ? "bg-blue-200 text-blue-800"
                  : "text-gray-700"
              }`}
              title="Strikethrough"
            >
              <s className="text-sm">S</s>
            </button>
          </div>

          <div className="w-px h-6 bg-gray-300 mx-1"></div>

          {/* Headings */}
          <div className="flex gap-1">
            <button
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 1 }).run()
              }
              className={`p-2 rounded hover:bg-gray-200 transition-colors text-xs font-bold ${
                editor.isActive("heading", { level: 1 })
                  ? "bg-blue-200 text-blue-800"
                  : "text-gray-700"
              }`}
              title="Heading 1"
            >
              H1
            </button>
            <button
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 2 }).run()
              }
              className={`p-2 rounded hover:bg-gray-200 transition-colors text-xs font-bold ${
                editor.isActive("heading", { level: 2 })
                  ? "bg-blue-200 text-blue-800"
                  : "text-gray-700"
              }`}
              title="Heading 2"
            >
              H2
            </button>
            <button
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 3 }).run()
              }
              className={`p-2 rounded hover:bg-gray-200 transition-colors text-xs font-bold ${
                editor.isActive("heading", { level: 3 })
                  ? "bg-blue-200 text-blue-800"
                  : "text-gray-700"
              }`}
              title="Heading 3"
            >
              H3
            </button>
          </div>

          <div className="w-px h-6 bg-gray-300 mx-1"></div>

          {/* Lists */}
          <div className="flex gap-1">
            <button
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                editor.isActive("bulletList")
                  ? "bg-blue-200 text-blue-800"
                  : "text-gray-700"
              }`}
              title="Bullet List"
            >
              <span className="text-sm">•</span>
            </button>
            <button
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                editor.isActive("orderedList")
                  ? "bg-blue-200 text-blue-800"
                  : "text-gray-700"
              }`}
              title="Numbered List"
            >
              <span className="text-sm">1.</span>
            </button>
          </div>

          <div className="w-px h-6 bg-gray-300 mx-1"></div>

          {/* Alignment */}
          <div className="flex gap-1">
            <button
              onClick={() => editor.chain().focus().setTextAlign("left").run()}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                editor.isActive({ textAlign: "left" })
                  ? "bg-blue-200 text-blue-800"
                  : "text-gray-700"
              }`}
              title="Align Left"
            >
              <span className="text-sm">←</span>
            </button>
            <button
              onClick={() =>
                editor.chain().focus().setTextAlign("center").run()
              }
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                editor.isActive({ textAlign: "center" })
                  ? "bg-blue-200 text-blue-800"
                  : "text-gray-700"
              }`}
              title="Align Center"
            >
              <span className="text-sm">↔</span>
            </button>
            <button
              onClick={() => editor.chain().focus().setTextAlign("right").run()}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                editor.isActive({ textAlign: "right" })
                  ? "bg-blue-200 text-blue-800"
                  : "text-gray-700"
              }`}
              title="Align Right"
            >
              <span className="text-sm">→</span>
            </button>
          </div>

          <div className="w-px h-6 bg-gray-300 mx-1"></div>

          {/* Colors */}
          <div className="flex gap-1">
            {colors.map((color) => (
              <button
                key={color}
                onClick={() => editor.chain().focus().setColor(color).run()}
                className="p-2 rounded hover:bg-gray-200 transition-colors"
                title={`Color: ${color}`}
                style={{ color }}
              >
                <span className="text-sm">A</span>
              </button>
            ))}
            <button
              onClick={() => editor.chain().focus().unsetColor().run()}
              className="p-2 rounded hover:bg-gray-200 transition-colors text-gray-700"
              title="Remove Color"
            >
              <span className="text-sm">🎨</span>
            </button>
          </div>

          <div className="w-px h-6 bg-gray-300 mx-1"></div>

          {/* Clear Format */}
          <button
            onClick={() =>
              editor.chain().focus().clearNodes().unsetAllMarks().run()
            }
            className="p-2 rounded hover:bg-gray-200 transition-colors text-gray-700"
            title="Clear Formatting"
          >
            <span className="text-sm">Clear</span>
          </button>
        </div>
      </div>
    );
  };

  const StudentCard = ({ student }) => (
    <div
      className={`bg-white rounded-lg shadow-md p-3 sm:p-4 border transition-all duration-200 w-full ${
        selectedStudents.includes(student.id)
          ? "border-blue-500 bg-blue-50 shadow-lg"
          : "border-gray-200 hover:shadow-lg"
      }`}
    >
      <div className="space-y-3">
        {/* Header with Photo, Status and Selection */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
            <Checkbox
              checked={selectedStudents.includes(student.id)}
              onChange={() => handleSelectStudent(student.id)}
              icon={<CheckBoxOutlineBlankIcon />}
              checkedIcon={<CheckBoxIcon />}
              className="text-blue-600"
            />
            <img
              src={
                student.profile_photo ||
                `https://ui-avatars.com/api/?name=${student.first_name}+${student.last_name}`
              }
              alt="avatar"
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover shadow-sm border flex-shrink-0"
            />
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">
                {student.first_name} {student.last_name}
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 truncate">
                {student.email}
              </p>
            </div>
          </div>
          <span
            className={`inline-flex items-center px-2 sm:px-3 py-1 rounded-full text-xs font-medium border flex-shrink-0 ${
              student.status === "Active"
                ? "bg-green-100 text-green-800 border-green-200"
                : "bg-red-100 text-red-800 border-red-200"
            }`}
          >
            {student.status}
          </span>
        </div>

        {/* Student Details */}
        <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs sm:text-sm">
            <div className="flex items-center gap-1">
              <span className="text-gray-500 whitespace-nowrap">
                Student ID:
              </span>
              <span className="font-mono text-gray-900 truncate">
                {student.id}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-500 whitespace-nowrap">Phone:</span>
              <a
                href={`tel:${student.phone_number}`}
                className="text-blue-600 hover:text-blue-800 underline truncate cursor-pointer"
                title="Click to call"
              >
                {student.phone_number}
              </a>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-1 text-xs sm:text-sm">
            <div className="flex items-center gap-2">
              <SchoolIcon className="text-gray-400 text-xs sm:text-sm flex-shrink-0" />
              <span className="text-gray-500 whitespace-nowrap">
                Education:
              </span>
              <span className="text-gray-900 truncate">
                {student.education}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
          <button
            onClick={() => handleViewStudent(student)}
            className="bg-blue-600 text-white px-2 sm:px-3 py-2 rounded-lg text-xs hover:bg-blue-700 transition-colors duration-200 shadow-sm flex-1 flex items-center justify-center gap-1 min-w-0"
          >
            <VisibilityIcon className="text-xs sm:text-sm flex-shrink-0" />
            <span className="truncate">View Details</span>
          </button>
          <button
            onClick={() => fetchInvoicesForStudent(student)}
            className="bg-yellow-500 text-white px-2 sm:px-3 py-2 rounded-lg text-xs hover:bg-yellow-600 transition-colors duration-200 shadow-sm flex-1 flex items-center justify-center gap-1 min-w-0"
          >
            <ListAltIcon className="text-xs sm:text-sm flex-shrink-0" />
            <span className="truncate">View Invoices</span>
          </button>
          {student.status === "Active" ? (
            <button
              onClick={() => confirmDelete(student.id)}
              className="bg-red-600 text-white px-2 sm:px-3 py-2 rounded-lg text-xs hover:bg-red-700 transition-colors duration-200 shadow-sm flex-1 flex items-center justify-center gap-1 min-w-0"
            >
              <BlockIcon className="text-xs sm:text-sm flex-shrink-0" />
              <span className="truncate">Deactivate</span>
            </button>
          ) : (
            <button
              onClick={() => handleActivate(student.id)}
              className="bg-green-600 text-white px-2 sm:px-3 py-2 rounded-lg text-xs hover:bg-green-700 transition-colors duration-200 shadow-sm flex-1 flex items-center justify-center gap-1 min-w-0"
            >
              <CheckCircleIcon className="text-xs sm:text-sm flex-shrink-0" />
              <span className="truncate">Activate</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Helper class for consistent height
  const uniformHeight = "h-12";

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 p-2 sm:p-4 md:p-4">
      <div className="w-full max-w-full mx-auto">
        {/* Header with Search, Filters, Email Button and Selection Controls */}
        <div className="mb-6 bg-white rounded-xl shadow-lg p-4 sm:p-3">
          {/* Title and Selection Controls */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <PeopleAltIcon className="text-purple-600 text-2xl" />
                <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
                  Student Management
                </h1>
              </div>
              {selectedStudents.length > 0 && (
                <Chip
                  label={`${selectedStudents.length} student(s) selected`}
                  color="primary"
                  variant="outlined"
                  className="bg-blue-50"
                />
              )}
            </div>

            <div className="flex items-center gap-3">
              {students.length > 0 &&
                !searchQuery &&
                statusFilter === "all" && (
                  <div className="flex items-center gap-2">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            selectedStudents.length === students.length &&
                            students.length > 0
                          }
                          indeterminate={
                            selectedStudents.length > 0 &&
                            selectedStudents.length < students.length
                          }
                          onChange={handleSelectAll}
                          icon={<CheckBoxOutlineBlankIcon />}
                          checkedIcon={<CheckBoxIcon />}
                        />
                      }
                      label="Select All"
                      className="text-sm"
                    />
                  </div>
                )}

              <button
                onClick={openEmailDialog}
                disabled={selectedStudents.length === 0}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium transition-all duration-200 shadow-sm ${
                  selectedStudents.length === 0
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700 hover:shadow-md"
                }`}
              >
                <EmailIcon className="text-sm" />
                <span className="hidden sm:inline">Send Email</span>
                <span className="sm:hidden">Email</span>
              </button>
            </div>
          </div>

          {/* Search and Filter Controls */}
          <div className="flex flex-col lg:flex-row gap-4 items-stretch lg:items-center w-full">
            {/* Search Input */}
            <div className="w-full">
              <div className="relative w-full">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search by name, phone, education, student ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    disabled={isSearching}
                    className={`block w-full pl-10 pr-3 border border-gray-300 rounded-xl text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${uniformHeight}`}
                    autoFocus
                  />
                </div>
              </div>
            </div>

            {/* Status Filter */}
            <div className="w-full lg:w-48">
              <FormControl fullWidth sx={{ height: "48px" }}>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  disabled={isSearching}
                  sx={{
                    borderRadius: "12px",
                    height: "48px",
                    minHeight: "48px",
                    "& .MuiSelect-select": {
                      height: "48px",
                      display: "flex",
                      alignItems: "center",
                      paddingTop: "0",
                      paddingBottom: "0",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#d1d5db",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#3b82f6",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#3b82f6",
                      borderWidth: "2px",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: "12px",
                      },
                    },
                  }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active Only</MenuItem>
                  <MenuItem value="inactive">Inactive Only</MenuItem>
                </Select>
              </FormControl>
            </div>

            {/* Search and Clear Buttons */}
            <div className="flex gap-2 w-full lg:w-auto">
              {(searchQuery || statusFilter !== "all") && (
                <button
                  onClick={handleClearSearch}
                  disabled={isSearching}
                  className={`flex items-center gap-2 px-4 bg-gray-500 text-white rounded-xl hover:bg-gray-600 transition-colors duration-200 shadow-sm disabled:bg-gray-400 disabled:cursor-not-allowed ${uniformHeight}`}
                  style={{
                    minWidth: "80px",
                    fontSize: "1rem",
                    fontWeight: 500,
                  }}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  <span className="hidden sm:inline">Clear</span>
                  <span className="sm:hidden">✕</span>
                </button>
              )}
            </div>
          </div>

          {/* Search Results Info */}
          {(searchQuery || statusFilter !== "all") && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-blue-800">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>
                  {searchQuery && `Searching for: "${searchQuery}"`}
                  {searchQuery && statusFilter !== "all" && " and "}
                  {statusFilter !== "all" &&
                    `Status: ${
                      statusFilter === "active" ? "Active" : "Inactive"
                    }`}
                  {!searchQuery &&
                    statusFilter !== "all" &&
                    `Filtering by status: ${
                      statusFilter === "active" ? "Active" : "Inactive"
                    }`}
                </span>
                <span className="text-blue-600 font-medium">
                  • {students.length} result{students.length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Mobile Cards View */}
        <div className="block md:hidden w-full">
          {students.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 text-center w-full">
              <div className="flex flex-col items-center gap-2">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  <PeopleAltIcon className="text-gray-400 text-2xl" />
                </div>
                <p className="text-gray-500 font-medium">No students found</p>
                <p className="text-gray-400 text-sm">
                  Try refreshing the page or check your connection
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4 w-full">
              {students.map((student) => (
                <StudentCard key={student.id} student={student} />
              ))}
            </div>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-hidden w-full">
          <div className="overflow-x-auto max-h-[calc(100vh-105px)] overflow-y-auto">
            <table className="min-w-full w-full">
              <thead className="bg-gradient-to-r from-purple-600 to-purple-700 text-white sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    {!searchQuery && statusFilter === "all" && (
                      <Checkbox
                        checked={
                          selectedStudents.length === students.length &&
                          students.length > 0
                        }
                        indeterminate={
                          selectedStudents.length > 0 &&
                          selectedStudents.length < students.length
                        }
                        onChange={handleSelectAll}
                        icon={<CheckBoxOutlineBlankIcon />}
                        checkedIcon={<CheckBoxIcon />}
                        sx={{
                          color: "white",
                          "&.Mui-checked": { color: "white" },
                        }}
                      />
                    )}
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Photo
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Student ID
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Name
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Phone
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Email
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Education
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Status
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {students.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-4 py-8 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                          <PeopleAltIcon className="text-gray-400 text-2xl" />
                        </div>
                        <p className="text-gray-500 font-medium">
                          No students found
                        </p>
                        <p className="text-gray-400 text-sm">
                          Try refreshing the page or check your connection
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  students.map((stu) => (
                    <tr
                      key={stu.id}
                      className={`transition-colors duration-200 ${
                        selectedStudents.includes(stu.id)
                          ? "bg-blue-50 hover:bg-blue-100"
                          : "hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-4 py-4">
                        <Checkbox
                          checked={selectedStudents.includes(stu.id)}
                          onChange={() => handleSelectStudent(stu.id)}
                          icon={<CheckBoxOutlineBlankIcon />}
                          checkedIcon={<CheckBoxIcon />}
                          className="text-blue-600"
                        />
                      </td>
                      <td className="px-4 py-4">
                        <img
                          src={
                            stu.profile_photo ||
                            `https://ui-avatars.com/api/?name=${stu.first_name}+${stu.last_name}`
                          }
                          alt="avatar"
                          className="w-10 h-10 rounded-full object-cover shadow-sm border"
                        />
                      </td>
                      <td className="px-4 py-4 font-mono text-gray-600">
                        {stu.id}
                      </td>
                      <td className="px-4 py-4">
                        <div className="font-medium text-gray-900">
                          {stu.first_name} {stu.last_name}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-gray-600">
                        <a
                          href={`tel:${stu.phone_number}`}
                          className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                          title="Click to call"
                        >
                          {stu.phone_number}
                        </a>
                      </td>
                      <td className="px-4 py-4 text-gray-600">{stu.email}</td>
                      <td className="px-4 py-4 text-gray-600">
                        {stu.education}
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${
                            stu.status === "Active"
                              ? "bg-green-100 text-green-800 border-green-200"
                              : "bg-red-100 text-red-800 border-red-200"
                          }`}
                        >
                          {stu.status}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleViewStudent(stu)}
                            className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-sm"
                            title="View Details"
                          >
                            <VisibilityIcon fontSize="small" />
                          </button>
                          <button
                            onClick={() => fetchInvoicesForStudent(stu)}
                            className="bg-yellow-500 text-white p-2 rounded-lg hover:bg-yellow-600 transition-colors duration-200 shadow-sm"
                            title="View Invoices"
                          >
                            <ListAltIcon fontSize="small" />
                          </button>
                          {stu.status === "Active" ? (
                            <button
                              onClick={() => confirmDelete(stu.id)}
                              className="bg-red-600 text-white p-2 rounded-lg hover:bg-red-700 transition-colors duration-200 shadow-sm"
                              title="Deactivate Student"
                            >
                              <BlockIcon fontSize="small" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleActivate(stu.id)}
                              className="bg-green-600 text-white p-2 rounded-lg hover:bg-green-700 transition-colors duration-200 shadow-sm"
                              title="Activate Student"
                            >
                              <CheckCircleIcon fontSize="small" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Student Details Modal */}
        <StudentBookingHistory
          open={viewModalOpen}
          onClose={() => setViewModalOpen(false)}
          studentId={selectedStudent?.id}
        />

        {/* Confirmation Modal */}
        {showConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 shadow-md w-80 max-w-[90vw] mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Are you sure?
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                This action will deactivate the student.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="px-4 py-2 text-gray-700 border rounded-md hover:bg-gray-100 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeactivate}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors duration-200"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Invoices Dialog */}
        <Dialog
          open={invoiceDialog.open}
          onClose={() => setInvoiceDialog((d) => ({ ...d, open: false }))}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            All Invoices for {invoiceDialog.student?.first_name}{" "}
            {invoiceDialog.student?.last_name}
          </DialogTitle>
          <DialogContent>
            {invoiceDialog.loading ? (
              <Typography>Loading...</Typography>
            ) : invoiceDialog.error ? (
              <Alert severity="error">{invoiceDialog.error}</Alert>
            ) : (
              <>
                <Tabs
                  value={invoiceDialog.tab}
                  onChange={(_, v) =>
                    setInvoiceDialog((d) => ({ ...d, tab: v }))
                  }
                  sx={{ mb: 2 }}
                >
                  <Tab label="Hostel Invoices" />
                  <Tab label="Library Invoices" />
                </Tabs>
                {/* Responsive: Table for md+, cards for mobile */}
                {invoiceDialog.tab === 0 && (
                  <>
                    <Box
                      className="hidden md:block"
                      sx={{ mb: 3, overflowX: "auto" }}
                    >
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="px-4 py-4 text-left">Invoice ID</th>
                            <th className="px-4 py-4 text-left">Month</th>
                            <th className="px-4 py-4 text-left">Amount</th>
                            <th className="px-4 py-4 text-left">Deposit</th>
                            <th className="px-4 py-4 text-left">Total</th>
                            <th className="px-4 py-4 text-left">Status</th>
                            <th className="px-4 py-4 text-left">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {invoiceDialog.hostel.length === 0 ? (
                            <tr>
                              <td
                                colSpan={7}
                                className="px-4 py-8 text-center text-gray-500"
                              >
                                No hostel invoices found.
                              </td>
                            </tr>
                          ) : (
                            [...invoiceDialog.hostel]
                              .sort((a, b) => {
                                if (a.generated_on && b.generated_on) {
                                  return (
                                    new Date(b.generated_on) -
                                    new Date(a.generated_on)
                                  );
                                }
                                return (b.id || 0) - (a.id || 0);
                              })
                              .map((inv) => (
                                <tr key={inv.id}>
                                  <td className="px-4 py-4 font-mono text-gray-600">
                                    {inv.invoice_id}
                                  </td>
                                  <td className="px-4 py-4 text-gray-600">
                                    {inv.month}
                                  </td>
                                  <td className="px-4 py-4 text-gray-600">
                                    ₹{inv.amount}
                                  </td>
                                  <td className="px-4 py-4 text-gray-600">
                                    ₹{inv.deposit}
                                  </td>
                                  <td className="px-4 py-4 font-bold text-gray-800">
                                    ₹{inv.total}
                                  </td>
                                  <td className="px-4 py-4">
                                    {inv.is_paid ? (
                                      <span className="inline-block px-2 py-1 rounded bg-green-100 text-green-800 text-xs font-semibold">
                                        Paid
                                      </span>
                                    ) : (
                                      <span className="inline-block px-2 py-1 rounded bg-yellow-100 text-yellow-800 text-xs font-semibold">
                                        Unpaid
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-4">
                                    {!inv.is_paid && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() =>
                                          handleMarkPaid("hostel", inv.id)
                                        }
                                        disabled={
                                          invoiceDialog.markingPaid[inv.id]
                                        }
                                      >
                                        {invoiceDialog.markingPaid[inv.id]
                                          ? "Marking..."
                                          : "Mark as Paid"}
                                      </Button>
                                    )}
                                  </td>
                                </tr>
                              ))
                          )}
                        </tbody>
                      </table>
                    </Box>
                    {/* Cards for mobile */}
                    <Box className="grid grid-cols-1 gap-4 md:hidden">
                      {invoiceDialog.hostel.length === 0 ? (
                        <div className="col-span-full text-center py-6 text-gray-500 bg-gray-50 rounded-xl shadow-inner">
                          No hostel invoices found.
                        </div>
                      ) : (
                        [...invoiceDialog.hostel]
                          .sort((a, b) => {
                            if (a.generated_on && b.generated_on) {
                              return (
                                new Date(b.generated_on) -
                                new Date(a.generated_on)
                              );
                            }
                            return (b.id || 0) - (a.id || 0);
                          })
                          .map((inv) => (
                            <div
                              key={inv.id}
                              className="bg-white rounded-xl shadow-md p-5 flex flex-col gap-3 border border-gray-100 hover:shadow-lg transition-shadow duration-200"
                            >
                              <div className="flex items-center gap-3 mb-2">
                                <ListAltIcon className="text-yellow-500 text-2xl" />
                                <div>
                                  <div className="font-bold text-lg text-gray-800">
                                    {inv.invoice_id}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {invoiceDialog.student?.first_name}{" "}
                                    {invoiceDialog.student?.last_name}
                                  </div>
                                </div>
                                <div className="ml-auto">
                                  {inv.is_paid ? (
                                    <span className="inline-block px-2 py-1 rounded bg-green-100 text-green-800 text-xs font-semibold">
                                      Paid
                                    </span>
                                  ) : (
                                    <span className="inline-block px-2 py-1 rounded bg-yellow-100 text-yellow-800 text-xs font-semibold">
                                      Unpaid
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2 text-sm text-gray-700">
                                <div className="flex-1 min-w-[120px]">
                                  <span className="font-medium">Month:</span>{" "}
                                  {inv.month}
                                </div>
                                <div className="flex-1 min-w-[120px]">
                                  <span className="font-medium">Amount:</span> ₹
                                  {inv.amount}
                                </div>
                                <div className="flex-1 min-w-[120px]">
                                  <span className="font-medium">Deposit:</span>{" "}
                                  ₹{inv.deposit}
                                </div>
                                <div className="flex-1 min-w-[120px]">
                                  <span className="font-medium">Total:</span>{" "}
                                  <span className="font-bold">
                                    ₹{inv.total}
                                  </span>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2 text-xs text-gray-500 mt-1">
                                <div>
                                  Generated:{" "}
                                  {new Date(inv.generated_on).toLocaleString()}
                                </div>
                                {inv.room_number && (
                                  <div>Room: {inv.room_number}</div>
                                )}
                                {inv.bed_number && (
                                  <div>Bed: {inv.bed_number}</div>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {!inv.is_paid && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() =>
                                      handleMarkPaid("hostel", inv.id)
                                    }
                                    disabled={invoiceDialog.markingPaid[inv.id]}
                                  >
                                    {invoiceDialog.markingPaid[inv.id]
                                      ? "Marking..."
                                      : "Mark as Paid"}
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))
                      )}
                    </Box>
                  </>
                )}
                {invoiceDialog.tab === 1 && (
                  <>
                    <Box
                      className="hidden md:block"
                      sx={{ mb: 1, overflowX: "auto" }}
                    >
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="px-4 py-4 text-left">Invoice ID</th>
                            <th className="px-4 py-4 text-left">Month</th>
                            <th className="px-4 py-4 text-left">Amount</th>
                            <th className="px-4 py-4 text-left">Deposit</th>
                            <th className="px-4 py-4 text-left">Total</th>
                            <th className="px-4 py-4 text-left">Status</th>
                            <th className="px-4 py-4 text-left">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {invoiceDialog.library.length === 0 ? (
                            <tr>
                              <td
                                colSpan={7}
                                className="px-4 py-8 text-center text-gray-500"
                              >
                                No library invoices found.
                              </td>
                            </tr>
                          ) : (
                            [...invoiceDialog.library]
                              .sort((a, b) => {
                                if (a.generated_on && b.generated_on) {
                                  return (
                                    new Date(b.generated_on) -
                                    new Date(a.generated_on)
                                  );
                                }
                                return (b.id || 0) - (a.id || 0);
                              })
                              .map((inv) => (
                                <tr key={inv.id}>
                                  <td className="px-4 py-4 font-mono text-gray-600">
                                    {inv.invoice_id}
                                  </td>
                                  <td className="px-4 py-4 text-gray-600">
                                    {inv.month}
                                  </td>
                                  <td className="px-4 py-4 text-gray-600">
                                    ₹{inv.amount}
                                  </td>
                                  <td className="px-4 py-4 text-gray-600">
                                    ₹{inv.deposit}
                                  </td>
                                  <td className="px-4 py-4 font-bold text-gray-800">
                                    ₹{inv.total}
                                  </td>
                                  <td className="px-4 py-4">
                                    {inv.is_paid ? (
                                      <span className="inline-block px-2 py-1 rounded bg-green-100 text-green-800 text-xs font-semibold">
                                        Paid
                                      </span>
                                    ) : (
                                      <span className="inline-block px-2 py-1 rounded bg-yellow-100 text-yellow-800 text-xs font-semibold">
                                        Unpaid
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-4">
                                    {!inv.is_paid && (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() =>
                                          handleMarkPaid("library", inv.id)
                                        }
                                        disabled={
                                          invoiceDialog.markingPaid[inv.id]
                                        }
                                      >
                                        {invoiceDialog.markingPaid[inv.id]
                                          ? "Marking..."
                                          : "Mark as Paid"}
                                      </Button>
                                    )}
                                  </td>
                                </tr>
                              ))
                          )}
                        </tbody>
                      </table>
                    </Box>
                    {/* Cards for mobile */}
                    <Box className="grid grid-cols-1 gap-4 md:hidden">
                      {invoiceDialog.library.length === 0 ? (
                        <div className="col-span-full text-center py-6 text-gray-500 bg-gray-50 rounded-xl shadow-inner">
                          No library invoices found.
                        </div>
                      ) : (
                        [...invoiceDialog.library]
                          .sort((a, b) => {
                            if (a.generated_on && b.generated_on) {
                              return (
                                new Date(b.generated_on) -
                                new Date(a.generated_on)
                              );
                            }
                            return (b.id || 0) - (a.id || 0);
                          })
                          .map((inv) => (
                            <div
                              key={inv.id}
                              className="bg-white rounded-xl shadow-md p-5 flex flex-col gap-3 border border-gray-100 hover:shadow-lg transition-shadow duration-200"
                            >
                              <div className="flex items-center gap-3 mb-2">
                                <ListAltIcon className="text-yellow-500 text-2xl" />
                                <div>
                                  <div className="font-bold text-lg text-gray-800">
                                    {inv.invoice_id}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {invoiceDialog.student?.first_name}{" "}
                                    {invoiceDialog.student?.last_name}
                                  </div>
                                </div>
                                <div className="ml-auto">
                                  {inv.is_paid ? (
                                    <span className="inline-block px-2 py-1 rounded bg-green-100 text-green-800 text-xs font-semibold">
                                      Paid
                                    </span>
                                  ) : (
                                    <span className="inline-block px-2 py-1 rounded bg-yellow-100 text-yellow-800 text-xs font-semibold">
                                      Unpaid
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2 text-sm text-gray-700">
                                <div className="flex-1 min-w-[120px]">
                                  <span className="font-medium">Month:</span>{" "}
                                  {inv.month}
                                </div>
                                <div className="flex-1 min-w-[120px]">
                                  <span className="font-medium">Amount:</span> ₹
                                  {inv.amount}
                                </div>
                                <div className="flex-1 min-w-[120px]">
                                  <span className="font-medium">Deposit:</span>{" "}
                                  ₹{inv.deposit}
                                </div>
                                <div className="flex-1 min-w-[120px]">
                                  <span className="font-medium">Total:</span>{" "}
                                  <span className="font-bold">
                                    ₹{inv.total}
                                  </span>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2 text-xs text-gray-500 mt-1">
                                <div>
                                  Generated:{" "}
                                  {new Date(inv.generated_on).toLocaleString()}
                                </div>
                                {inv.seat_number && (
                                  <div>Seat: {inv.seat_number}</div>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-2 mt-2">
                                {!inv.is_paid && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() =>
                                      handleMarkPaid("library", inv.id)
                                    }
                                    disabled={invoiceDialog.markingPaid[inv.id]}
                                  >
                                    {invoiceDialog.markingPaid[inv.id]
                                      ? "Marking..."
                                      : "Mark as Paid"}
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))
                      )}
                    </Box>
                  </>
                )}
              </>
            )}
          </DialogContent>
          <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2 }}>
            {/* Generate Invoice Button */}
            {invoiceDialog.student &&
              ((invoiceDialog.tab === 0 &&
                (() => {
                  return (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<ReceiptOutlinedIcon />}
                      onClick={() => handleGenerateInvoice("hostel")}
                      disabled={generatingInvoice}
                      sx={{ mr: 2 }}
                    >
                      {generatingInvoice ? "Generating..." : "Generate Invoice"}
                    </Button>
                  );
                })()) ||
                (invoiceDialog.tab === 1 &&
                  (() => {
                    return (
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<ReceiptOutlinedIcon />}
                        onClick={() => handleGenerateInvoice("library")}
                        disabled={generatingInvoice}
                        sx={{ mr: 2 }}
                      >
                        {generatingInvoice
                          ? "Generating..."
                          : "Generate Invoice"}
                      </Button>
                    );
                  })()))}
            <Button
              onClick={() => setInvoiceDialog((d) => ({ ...d, open: false }))}
            >
              Close
            </Button>
          </Box>
        </Dialog>

        {/* Email Dialog */}
        <Dialog
          open={emailDialog.open}
          onClose={() => setEmailDialog((prev) => ({ ...prev, open: false }))}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "16px",
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            },
          }}
        >
          <DialogTitle className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <EmailIcon className="text-white text-xl" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Send Email to Students</h2>
                <p className="text-blue-100 text-sm mt-1">
                  Compose and send emails to selected students
                </p>
              </div>
            </div>
          </DialogTitle>
          <DialogContent className="p-6">
            {emailDialog.error && (
              <Alert
                severity="error"
                sx={{
                  mb: 3,
                  borderRadius: "12px",
                  "& .MuiAlert-icon": { fontSize: "20px" },
                }}
              >
                {emailDialog.error}
              </Alert>
            )}

            <div className="space-y-6 mt-3">
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Subject *
                </label>
                <TextField
                  fullWidth
                  value={emailDialog.subject}
                  onChange={(e) =>
                    setEmailDialog((prev) => ({
                      ...prev,
                      subject: e.target.value,
                    }))
                  }
                  required
                  placeholder="Enter email subject..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#3b82f6",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#3b82f6",
                        borderWidth: "2px",
                      },
                    },
                  }}
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Message *
                </label>
                <div className="border border-gray-300 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
                  <TipTapToolbar />
                  <div
                    className="min-h-[200px] bg-white cursor-text"
                    onClick={() => editor?.chain().focus().run()}
                  >
                    <EditorContent
                      editor={editor}
                      className="min-h-[200px] p-4 focus:outline-none bg-white"
                      style={{ minHeight: "200px" }}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Target Group
                </label>
                <FormControl fullWidth>
                  <Select
                    value={emailDialog.targetGroup}
                    onChange={(e) =>
                      setEmailDialog((prev) => ({
                        ...prev,
                        targetGroup: e.target.value,
                      }))
                    }
                    sx={{
                      borderRadius: "12px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#d1d5db",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#3b82f6",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#3b82f6",
                        borderWidth: "2px",
                      },
                    }}
                  >
                    <MenuItem value="both">
                      <div className="flex items-center gap-2">
                        <span className="text-blue-600">👥</span>
                        All Students (Hostel + Library)
                      </div>
                    </MenuItem>
                    <MenuItem value="hostel">
                      <div className="flex items-center gap-2">
                        <span className="text-green-600">🏨</span>
                        Hostel Students Only
                      </div>
                    </MenuItem>
                    <MenuItem value="library">
                      <div className="flex items-center gap-2">
                        <span className="text-purple-600">📚</span>
                        Library Students Only
                      </div>
                    </MenuItem>
                  </Select>
                </FormControl>
              </div>

              {selectedStudents.length > 0 && (
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-1 bg-blue-100 rounded-full">
                      <EmailIcon className="text-blue-600 text-sm" />
                    </div>
                    <Typography
                      variant="subtitle2"
                      className="text-blue-800 font-semibold"
                    >
                      {selectedStudents.length === students.length &&
                      students.length > 0
                        ? "📧 All Students Selected"
                        : `📧 Selected Students (${selectedStudents.length})`}
                    </Typography>
                  </div>
                  {selectedStudents.length === students.length &&
                  students.length > 0 ? (
                    <p className="text-blue-700 text-sm">
                      Email will be sent to all students in the target group
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {students
                        .filter((student) =>
                          selectedStudents.includes(student.id)
                        )
                        .map((student) => (
                          <Chip
                            key={student.id}
                            label={`${student.first_name} ${student.last_name}`}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: "#3b82f6",
                              color: "#3b82f6",
                              "&:hover": {
                                backgroundColor: "#eff6ff",
                              },
                            }}
                          />
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              p: 3,
              gap: 2,
              borderTop: "1px solid #e5e7eb",
              backgroundColor: "#f9fafb",
            }}
          >
            <Button
              onClick={() =>
                setEmailDialog((prev) => ({ ...prev, open: false }))
              }
              disabled={emailDialog.loading}
              variant="outlined"
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                py: 1.5,
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSendEmail}
              disabled={emailDialog.loading}
              startIcon={emailDialog.loading ? null : <EmailIcon />}
              sx={{
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                py: 1.5,
                background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
                },
                "&:disabled": {
                  background: "#9ca3af",
                },
              }}
            >
              {emailDialog.loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Sending...
                </div>
              ) : (
                "Send Email"
              )}
            </Button>
          </Box>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminStudentList;
